import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import IntervalInput from "../../../../components/inputs/IntervalInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import { sameVietaSet, sameVietaValue } from "./vietaAnswerSets";
import { selectExtendedVariant } from "../../../extendedVariantSelection";


const TYPE = "MaturaQuadraticVietaParamOpenTask";

const VARIANTS = ["vieta-equation", "same-sign-distance", "square-difference"];

const SOURCES = {
  "vieta-equation": { session: "2025_CZE", taskNumber: "5", basedOn: "MMAP-R0-100-2506-5" },
  "same-sign-distance": { session: "2025_MAJ", taskNumber: "11", basedOn: "MMAP-R0-100-A-2505-11" },
  "square-difference": { session: "2024_GRU", taskNumber: "11", basedOn: "MMAP-R0-100-2412-11" },
};

const DISTANCES = [
  { limit: 48, low: 9 / 7, high: 7, lowLatex: "\\frac{9}{7}", highLatex: "7" },
  { limit: 65, low: 4 / 3, high: 16 / 3, lowLatex: "\\frac{4}{3}", highLatex: "\\frac{16}{3}" },
  { limit: 180, low: 3 / 2, high: 13 / 4, lowLatex: "\\frac{3}{2}", highLatex: "\\frac{13}{4}" },
];

const PARAMETER_SCALES = [1, 2, 3, 4];
const PARAMETER_SHIFTS = [-3, -2, -1, 0, 1, 2, 3];
const SQUARE_SUMS = [1, 3, 5];
const SQUARE_GAPS = [2, 3, 4];

const AFFINE_CHOICES = PARAMETER_SCALES.flatMap((a) =>
  PARAMETER_SHIFTS.map((b) => ({ a, b }))
);

// Warianty zadania 5 tworzymy bez ukrytego podstawienia u = am + b.
// Trzy współczynniki trójmianu i mnożnik w warunku zmieniają się niezależnie;
// stałą wybieramy tak, by jedynym dopuszczalnym parametrem był root.
const DIRECT_VIETA_CONFIGS = [1, 2, 3].flatMap((p) =>
  [1, 2, 3].flatMap((r) =>
    [-3, -2, -1, 0, 1, 2].flatMap((s) =>
      [2, 3, 4].flatMap((k) =>
        [-2, -1, 1, 2].flatMap((root) => {
          const leading = p * p;
          const middle = -(k + 2) * r;
          const linear = -(k + 2) * s;
          const constant = leading * root ** 3 + middle * root ** 2 + linear * root;
          const qMiddle = middle + leading * root;
          const qConstant = linear + middle * root + leading * root * root;
          const distinctRootsAtAnswer = (p * root) ** 2 - 4 * (r * root + s) > 0;
          const uniqueRealAnswer = qMiddle ** 2 - 4 * leading * qConstant < 0;
          const isOriginal = p === 2 && r === 2 && s === -1 && k === 3 && root === 2;
          return distinctRootsAtAnswer && uniqueRealAnswer && constant !== 0
            && Math.abs(constant) <= 24 && !isOriginal
            ? [{ mode: "direct", p, r, s, k, root, constant }]
            : [];
        })))));

const VARIANT_CONFIGS = {
  "same-sign-distance": AFFINE_CHOICES.flatMap(({ a, b }) =>
    DISTANCES.filter(({ limit }) => a !== 1 || b !== 0 || limit !== 180)
      .map(({ limit }) => ({ a, b, limit }))),
  "square-difference": AFFINE_CHOICES.flatMap(({ a, b }) =>
    SQUARE_SUMS.flatMap((sum) =>
      SQUARE_GAPS.filter((gap) => a !== 1 || b !== 0 || sum !== 3 || gap !== 4)
        .map((gap) => ({ a, b, sum, gap, limit: sum * gap })))),
};

export function getVariantCounts() {
  return { "vieta-equation": DIRECT_VIETA_CONFIGS.length,
    "same-sign-distance": VARIANT_CONFIGS["same-sign-distance"].length,
    "square-difference": VARIANT_CONFIGS["square-difference"].length };
}

export function getDirectVietaConfigs() {
  return DIRECT_VIETA_CONFIGS.map((config) => ({ ...config }));
}

function linearLatex(a, b, variable = "m") {
  const term = a === 0 ? "" : a === 1 ? variable : a === -1 ? `-${variable}` : `${a}${variable}`;
  if (b === 0) return term || "0";
  return `${term}${term && b > 0 ? "+" : ""}${b}`;
}

function signedMonomial(coefficient, variable) {
  if (coefficient === 0) return "";
  const sign = coefficient < 0 ? "-" : "+";
  const magnitude = Math.abs(coefficient);
  return `${sign}${magnitude === 1 ? "" : magnitude}${variable}`;
}

function signedConstant(value) {
  return value < 0 ? String(value) : value > 0 ? `+${value}` : "";
}

function constantMinusParameterLatex(constant, scale) {
  const parameter = `${scale === 1 ? "" : scale}m`;
  return constant === 0 ? `-${parameter}` : `${constant}-${parameter}`;
}

function expandedSquarePolynomialLatex({ a, b, sum }) {
  const constant = (sum * sum + 3) / 4 - b * b + b;
  return `f(x)=x^2${signedMonomial(-sum, "x")}${signedMonomial(-a * a, "m^2")}`
    + `${signedMonomial(a * (1 - 2 * b), "m")}${constant < 0 ? constant : constant > 0 ? `+${constant}` : ""}`;
}

function rationalLatex(num, den = 1) {
  const gcd = (x, y) => y === 0 ? Math.abs(x) : gcd(y, x % y);
  const divisor = gcd(num, den);
  const n = num / divisor;
  const d = den / divisor;
  return d === 1 ? String(n) : `\\frac{${n}}{${d}}`;
}

function mEndpointLatex(uValue, a, b, uLatex) {
  if (a === 1 && b === 0) return uLatex;
  for (let den = 1; den <= 20; den++) {
    const num = Math.round(uValue * den);
    if (Math.abs(num / den - uValue) < 1e-9) return rationalLatex(num - b * den, a * den);
  }
  const numerator = b === 0 ? uLatex : `${uLatex}${b > 0 ? `-${b}` : `+${-b}`}`;
  return `\\frac{${numerator}}{${a}}`;
}

function squareMEndpointLatex(d, sign) {
  const radical = d.gap === 2 ? "2\\sqrt2" : d.gap === 3 ? "\\sqrt{13}" : "2\\sqrt5";
  const constant = 1 - 2 * d.b;
  const numerator = `${constant}${sign < 0 ? "-" : "+"}${radical}`;
  return `\\frac{${numerator}}{${2 * d.a}}`;
}

function mapIntervalToM(item, a, b) {
  return interval(
    Number.isFinite(item.left) ? (item.left - b) / a : item.left,
    Number.isFinite(item.right) ? (item.right - b) / a : item.right,
    item.leftClosed,
    item.rightClosed
  );
}

function mapSetsToM(sets, a, b) {
  return Object.fromEntries(Object.entries(sets).map(([key, values]) =>
    [key, values.map((item) => mapIntervalToM(item, a, b))]));
}

function squareData(gap, sum) {
  const radical = { 2: 2, 3: 13, 4: 20 }[gap];
  const rootLatex = radical === 2 ? "2\\sqrt2" : radical === 20 ? "2\\sqrt5" : "\\sqrt{13}";
  const rootInput = radical === 2 ? "2sqrt2" : radical === 20 ? "2sqrt5" : "sqrt13";
  const halfRoot = Math.sqrt(4 + gap * gap);
  return {
    limit: sum * gap, gap, low: (1 - halfRoot) / 2, high: (1 + halfRoot) / 2,
    lowLatex: `\\frac{1-${rootLatex}}{2}`, highLatex: `\\frac{1+${rootLatex}}{2}`,
    lowInput: `(1-${rootInput})/2`, highInput: `(1+${rootInput})/2`,
  };
}

function getData(task) {
  if (task.mode === "direct" && task.variant === "vieta-equation") return task;
  const a = task.a || 1;
  const b = task.b || 0;
  if (task.variant === "same-sign-distance") return { ...(DISTANCES.find((item) => item.limit === task.limit) || DISTANCES[2]), a, b };
  if (task.variant === "square-difference") {
    const sum = task.sum || 3;
    return { ...squareData(task.gap || (task.limit || 12) / sum, sum), sum, a, b };
  }
  const root = task.root ?? 2;
  return { root, constant: root * (4 * root * root - 10 * root + 5), a, b };
}

function interval(left, right, leftClosed = false, rightClosed = false) {
  return { left, right, leftClosed, rightClosed };
}

function expectedSets(task) {
  const d = getData(task);
  if (task.variant === "same-sign-distance") {
    return mapSetsToM({
      delta: [interval(-Infinity, -3), interval(1, Infinity)],
      sameSign: [interval(-8, 2)],
      condition: [interval(-Infinity, d.low, false, true), interval(d.high, Infinity, true, false)],
      final: [interval(-8, -3), interval(1, d.low, false, true)],
    }, d.a, d.b);
  }
  return mapSetsToM({
    delta: [interval(-Infinity, -0.5), interval(1.5, Infinity)],
    condition: [interval(d.low, d.high, true, true)],
    final: [interval(d.low, -0.5, true, false), interval(1.5, d.high, false, true)],
  }, d.a, d.b);
}

function rand(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function rawValue(value) {
  if (value == null) return "";
  if (typeof value === "object" && value.target && "value" in value.target) {
    return String(value.target.value ?? "");
  }
  if (typeof value === "object") {
    for (const key of ["raw", "text", "value", "latex", "expr"]) {
      if (value[key] != null && typeof value[key] !== "object") {
        return String(value[key]);
      }
    }
  }
  return String(value);
}

function normalize(raw) {
  return rawValue(raw)
    .toLowerCase()
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot|\\times|·|×/g, "*")
    .replace(/\\sqrt/g, "sqrt")
    .replace(/\\infty|∞/g, "inf")
    .replace(/\\cup|∪/g, "u")
    .replace(/\\ne|\\neq|≠/g, "!=")
    .replace(/\\leq|≤/g, "<=")
    .replace(/\\geq|≥/g, ">=")
    .replace(/\\mathbb\{r\}|ℝ/g, "r")
    .replace(/[{}]/g, "")
    .replace(/,/g, ".")
    .replace(/\s+/g, "");
}

function toLatexExpression(raw) {
  const s = rawValue(raw).trim();
  if (!s) return "?";
  return s
    .replace(/,/g, "{,}")
    .replace(/\*/g, "\\cdot ")
    .replace(/\^\(([^)]+)\)/g, "^{$1}")
    .replace(/\^(\d+)/g, "^{$1}")
    .replace(/<=/g, "\\le ")
    .replace(/>=/g, "\\ge ")
    .replace(/!=/g, "\\ne ")
    .replace(/sqrt\(([^)]+)\)/gi, "\\sqrt{$1}")
    .replace(/sqrt(\d+)/gi, "\\sqrt{$1}");
}

function checkDistanceTransform6(raw) {
  const s = normalize(raw);
  return ["(x1-x2)^2=(x1+x2)^2-4x1x2", "(x1-x2)^2=delta/a^2", "(x1-x2)^2=\\delta/a^2"].includes(s);
}

function setField(answer, key, value, onChange) {
  onChange && onChange({ ...(answer || {}), [key]: value });
}

function getVariantMeta(variant) {
  if (variant === "same-sign-distance") {
    return { points: 6, label: "warunek znaku i odległości pierwiastków" };
  }
  if (variant === "square-difference") {
    return { points: 5, label: "warunek z wartością bezwzględną" };
  }
  return { points: 4, label: "warunek z sumą kwadratów pierwiastków" };
}

export function generateTask(options = {}) {
  const variant = rand(VARIANTS);
  return createTask(variant, TYPE, options);
}

export function createTask(variant, type = TYPE, options = {}) {
  const meta = getVariantMeta(variant);
  const source = SOURCES[variant];
  const isOriginal = options.variant === "original";
  const data = isOriginal
    ? variant === "same-sign-distance" ? { a: 1, b: 0, limit: 180 }
      : variant === "square-difference" ? { a: 1, b: 0, sum: 3, gap: 4, limit: 12 }
        : { a: 1, b: 0, root: 2 }
    : options.config || selectExtendedVariant(variant === "vieta-equation" ? DIRECT_VIETA_CONFIGS : VARIANT_CONFIGS[variant], options);
  return {
    type,
    variant,
    points: meta.points,
    label: meta.label,
    ...data,
    source: source ? { variant: isOriginal ? "original" : "random", exam: "MATURA", ...source } : undefined,
  };
}

function renderVietaEquationTask(task) {
  const { constant, a, b } = getData(task);
  const direct = task.mode === "direct";
  const u = direct ? "" : linearLatex(a, b);
  return (
    <>
      <p>
        Wyznacz wszystkie wartości parametru <InlineMath math="m" />, dla których
        równanie
      </p>
      <BlockMath math={direct
        ? `x^2${signedMonomial(task.p, "mx")}${signedMonomial(task.r, "m")}${signedConstant(task.s)}=0`
        : task.source?.variant === "original"
        ? "x^2+2mx+2m-1=0"
        : `x^2+(${linearLatex(2 * a, 2 * b)})x+(${linearLatex(2 * a, 2 * b - 1)})=0`} />
      <p>
        ma dwa różne rozwiązania rzeczywiste <InlineMath math="x_1" /> i{" "}
        <InlineMath math="x_2" /> spełniające warunek
      </p>
      <BlockMath math={direct
        ? `m(x_1^2+x_2^2)=${task.k}m\\cdot x_1\\cdot x_2${signedConstant(constant)}`
        : task.source?.variant === "original"
        ? "m(x_1^2+x_2^2)=3m\\cdot x_1\\cdot x_2+2"
        : `(${u})(x_1^2+x_2^2)=3(${u})\\cdot x_1\\cdot x_2${constant < 0 ? constant : `+${constant}`}`} />
      <p>
        <strong>Zapisz obliczenia.</strong>
      </p>
    </>
  );
}

function renderSameSignTask(task) {
  const { a, b, limit } = getData(task);
  const analogue = `f(x)=(${constantMinusParameterLatex(2 - b, a)})x^2-2(${linearLatex(2 * a, 2 * b + 1)})x+${linearLatex(a, b + 8)}`;
  return (
    <>
      <p>
        Funkcja <InlineMath math="f" /> jest określona wzorem
      </p>
      <BlockMath math={task.source?.variant === "original"
        ? "f(x)=(2-m)x^2-2(2m+1)x+m+8"
        : analogue} />
      <p>
        dla każdej liczby rzeczywistej <InlineMath math="x" />, gdzie{" "}
        <InlineMath math="m" /> jest liczbą rzeczywistą różną od{" "}
        <InlineMath math={rationalLatex(2 - b, a)} />.
      </p>
      <p>
        Wyznacz wszystkie wartości parametru <InlineMath math="m" />, dla których
        funkcja <InlineMath math="f" /> ma dokładnie dwa miejsca zerowe{" "}
        <InlineMath math="x_1" /> oraz <InlineMath math="x_2" /> tego samego
        znaku, które spełniają warunek
      </p>
      <BlockMath math={`(x_1-x_2)^2\\le ${limit}`} />
      <p>
        <strong>Zapisz obliczenia.</strong>
      </p>
    </>
  );
}

function renderSquareDifferenceTask(task) {
  const { a, b, sum, limit } = getData(task);
  return (
    <>
      <p>
        Funkcja kwadratowa <InlineMath math="f" /> zmiennej rzeczywistej{" "}
        <InlineMath math="x" /> jest określona wzorem
      </p>
      <BlockMath math={task.source?.variant === "original"
        ? "f(x)=x^2-3x-m^2+m+3"
        : expandedSquarePolynomialLatex({ a, b, sum })} />
      <p>
        Wyznacz wszystkie wartości parametru <InlineMath math="m" />, dla których
        funkcja <InlineMath math="f" /> ma dwa różne miejsca zerowe{" "}
        <InlineMath math="x_1" />, <InlineMath math="x_2" /> spełniające warunek
      </p>
      <BlockMath math={`|x_1^2-x_2^2|\\le ${limit}`} />
      <p>
        <strong>Zapisz obliczenia.</strong>
      </p>
    </>
  );
}

export function renderTask(task) {
  return (
    <div className="space-y-4">
      <p>
        <strong>Zadanie maturalne - otwarte (0-{task.points} pkt).</strong>
      </p>
      {task.variant === "same-sign-distance"
        ? renderSameSignTask(task)
        : task.variant === "square-difference"
          ? renderSquareDifferenceTask(task)
          : renderVietaEquationTask(task)}
    </div>
  );
}

function InputRow({ label, value, onChange, disabled, placeholder, basis = "520px", interval = false, variable = "m" }) {
  return (
    <div className="space-y-2">
      <p>
        <strong>{label}</strong>
      </p>
      {interval
        ? <IntervalInput
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder.replace(/^np\.\s*[mu]\s+in\s+/i, "np. ").replace(/,/g, ";").replace(/ U /g, " ∪ ")}
          label={label}
          showPreview
          asString
          previewVariable={variable}
          showRootFractionTools
        />
        : <EquationInput
          value={value || ""}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          showPreview={true}
          basis={basis}
        />}
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const d = getData(task);
  if (task.mode === "direct") {
    return <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <p>Rozwiązuj bez dodatkowego podstawienia. W kolejnych polach możesz zapisać własne obliczenia; w ostatnim podaj wartość parametru.</p>
      <InputRow label="Krok 1. Warunek dwóch różnych pierwiastków" value={userAnswer.delta}
        onChange={(value) => setField(userAnswer, "delta", value, onChange)} disabled={disabled}
        placeholder={`np. Δ=(${d.p === 1 ? "m" : `${d.p}m`})^2-4(${linearLatex(d.r, d.s)})>0`} />
      <InputRow label="Krok 2a. Suma kwadratów z wzorów Viète’a" value={userAnswer.vietaTransform}
        onChange={(value) => setField(userAnswer, "vietaTransform", value, onChange)} disabled={disabled}
        placeholder="np. x1^2+x2^2=(x1+x2)^2-2x1x2" />
      <InputRow label="Krok 2b. Równanie z jedną niewiadomą m" value={userAnswer.equation}
        onChange={(value) => setField(userAnswer, "equation", value, onChange)} disabled={disabled}
        placeholder="np. ...m^3+...m^2+...m+...=0" />
      <InputRow label="Krok 3. Odpowiedź dla m" value={userAnswer.final}
        onChange={(value) => setField(userAnswer, "final", value, onChange)} disabled={disabled}
        placeholder="np. m=..." basis="320px" />
    </div>;
  }
  if (task.source?.variant === "random") {
    const fields = task.variant === "vieta-equation"
      ? [
        ["delta", "Krok 1. Warunek dwóch różnych pierwiastków w u", "np. u!=1"],
        ["vietaTransform", "Krok 2a. Suma kwadratów pierwiastków", "np. x1^2+x2^2=(x1+x2)^2-2x1x2"],
        ["equation", "Krok 2b. Równanie w u", "np. u[(-2u)^2-2(2u-1)]=..."],
        ["final", "Krok 3. Odpowiedź dla m", "np. m=..."],
      ]
      : task.variant === "same-sign-distance"
        ? [
          ["delta", "Krok 1. Warunek dwóch miejsc zerowych w u", "np. u in (-inf,-3) U (1,inf)"],
          ["sameSign", "Krok 2. Warunek tego samego znaku", "np. (u+8)/(2-u)>0"],
          ["distanceTransform", "Krok 3a. Wzór na różnicę pierwiastków", "np. (x1-x2)^2=(x1+x2)^2-4x1x2"],
          ["distanceInequality", "Krok 3b. Nierówność w u", `np. 20(u+3)(u-1)/(2-u)^2<=${d.limit}`],
          ["distanceSolution", "Krok 3c. Rozwiązanie nierówności w u", "np. u in (-inf,a] U [b,inf)"],
          ["final", "Krok 4. Odpowiedź dla m", "np. m in (a,b) U (c,d]"],
        ]
        : [
          ["delta", "Krok 1. Warunek dwóch miejsc zerowych w u", "np. (-inf;-1/2) U (3/2;inf)"],
          ["absTransform", "Krok 2a. Przekształcenie różnicy kwadratów", `np. |x1-x2|*|x1+x2|<=${d.limit}`],
          ["conditionEquation", "Krok 2b. Nierówność w u", `np. 4u^2-4u-3<=${d.gap ** 2}`],
          ["conditionSolution", "Krok 2c. Przedział z nierówności", "np. u in [(1-√7)/2; (1+√7)/2]"],
          ["final", "Krok 3. Odpowiedź dla m", "np. m in [(1-√5)/2; -1/2) U (3/2; (1+√5)/2]"],
        ];
    return <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <p>Możesz liczyć po podstawieniu <InlineMath math={`u=${linearLatex(d.a, d.b)}`} />. W ostatnim polu podaj rozwiązanie dla <InlineMath math="m" />.</p>
      {task.variant !== "vieta-equation" && <p>Przedziały wpisuj bez symbolu zmiennej, oddzielając końce średnikiem. Ułamek z pierwiastkiem możesz zapisać np. jako <code>(1-√13)/2</code>.</p>}
      {fields.map(([key, label, placeholder]) => <InputRow key={key} label={label} value={userAnswer[key]}
        onChange={(value) => setField(userAnswer, key, value, onChange)} disabled={disabled} placeholder={placeholder}
        interval={task.variant !== "vieta-equation" && ["delta", "distanceSolution", "conditionSolution", "final"].includes(key)}
        variable={key === "final" ? "m" : "u"} />)}
    </div>;
  }
  if (task.variant === "same-sign-distance") {
    return (
      <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
        <p>Przedziały wpisuj bez <code>m ∈</code>, oddzielając końce średnikiem. Ułamek z pierwiastkiem możesz zapisać np. jako <code>(1-√13)/2</code>.</p>
        <InputRow
          label="Krok 1 (1 pkt). Rozwiaz warunek dwoch roznych miejsc zerowych."
          value={userAnswer.delta}
          onChange={(v) => setField(userAnswer, "delta", v, onChange)}
          disabled={disabled}
          placeholder="np. m in (-inf,-3) U (1,inf)"
          interval
        />
        <InputRow
          label="Krok 2 (1 pkt). Zapisz warunek tego samego znaku miejsc zerowych."
          value={userAnswer.sameSign}
          onChange={(v) => setField(userAnswer, "sameSign", v, onChange)}
          disabled={disabled}
          placeholder="np. (m+8)/(2-m)>0, wiec m in (-8,2)"
        />
        <InputRow
          label="Krok 3a (1 pkt). Przeksztalc warunek z odlegloscia pierwiastkow do postaci z wzorami Viete'a."
          value={userAnswer.distanceTransform}
          onChange={(v) => setField(userAnswer, "distanceTransform", v, onChange)}
          disabled={disabled}
          placeholder="np. (x1-x2)^2=(x1+x2)^2-4x1x2"
        />
        <InputRow
          label="Krok 3b (1 pkt). Zapisz nierownosc z jedna niewiadoma m."
          value={userAnswer.distanceInequality}
          onChange={(v) => setField(userAnswer, "distanceInequality", v, onChange)}
          disabled={disabled}
          placeholder={`np. [2(2m+1)/(2-m)]^2-4(m+8)/(2-m)<=${d.limit}`}
        />
        <InputRow
          label="Krok 3c (1 pkt). Rozwiaz ten warunek."
          value={userAnswer.distanceSolution}
          onChange={(v) => setField(userAnswer, "distanceSolution", v, onChange)}
          disabled={disabled}
          placeholder="np. m in (-inf,a] U [b,inf)"
          interval
        />
        <InputRow
          label="Krok 4 (1 pkt). Podaj czesc wspolna warunkow."
          value={userAnswer.final}
          onChange={(v) => setField(userAnswer, "final", v, onChange)}
          disabled={disabled}
          placeholder="np. m in (-8,-3) U (1,a]"
          interval
        />
      </div>
    );
  }

  if (task.variant === "square-difference") {
    return (
      <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
        <p>Przedziały wpisuj bez <code>m ∈</code>, oddzielając końce średnikiem. Ułamek z pierwiastkiem możesz zapisać np. jako <code>(1-√13)/2</code>.</p>
        <InputRow
          label="Krok 1 (1 pkt). Rozwiaz warunek dwoch roznych miejsc zerowych."
          value={userAnswer.delta}
          onChange={(v) => setField(userAnswer, "delta", v, onChange)}
          disabled={disabled}
          placeholder="np. m in (-inf,-1/2) U (3/2,inf)"
          interval
        />
        <InputRow
          label="Krok 2a (1 pkt). Przeksztalc warunek z wartosci bezwzglednej do postaci z suma i roznica pierwiastkow."
          value={userAnswer.absTransform}
          onChange={(v) => setField(userAnswer, "absTransform", v, onChange)}
          disabled={disabled}
          placeholder={`np. |x1-x2|*|x1+x2|<=${d.limit}`}
        />
        <InputRow
          label="Krok 2b (1 pkt). Zapisz nierownosc z jedna niewiadoma m."
          value={userAnswer.conditionEquation}
          onChange={(v) => setField(userAnswer, "conditionEquation", v, onChange)}
          disabled={disabled}
          placeholder={`np. 9-4(-m^2+m+3)<=${(d.limit / 3) ** 2}`}
        />
        <InputRow
          label="Krok 2c (1 pkt). Rozwiaz ten warunek."
          value={userAnswer.conditionSolution}
          onChange={(v) => setField(userAnswer, "conditionSolution", v, onChange)}
          disabled={disabled}
          placeholder="np. m in [(1-√7)/2; (1+√7)/2]"
          interval
        />
        <InputRow
          label="Krok 3 (1 pkt). Podaj czesc wspolna warunkow."
          value={userAnswer.final}
          onChange={(v) => setField(userAnswer, "final", v, onChange)}
          disabled={disabled}
          placeholder="np. [(1-√5)/2; -1/2) U (3/2; (1+√5)/2]"
          interval
        />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <OpenTaskScoringHint variant="independent" />
      <InputRow
        label="Krok 1 (1 pkt). Zapisz warunek dwoch roznych pierwiastkow."
        value={userAnswer.delta}
        onChange={(v) => setField(userAnswer, "delta", v, onChange)}
        disabled={disabled}
        placeholder="np. m != 1"
        basis="320px"
      />
      <InputRow
        label="Krok 2a (1 pkt). Zapisz wyrazenie po zastosowaniu wzorow Viete'a."
        value={userAnswer.vietaTransform}
        onChange={(v) => setField(userAnswer, "vietaTransform", v, onChange)}
        disabled={disabled}
        placeholder="np. x1^2+x2^2=(-2m)^2-2(2m-1)"
      />
      <InputRow
        label="Krok 2b (1 pkt). Zapisz rownanie z jedna niewiadoma m."
        value={userAnswer.equation}
        onChange={(v) => setField(userAnswer, "equation", v, onChange)}
        disabled={disabled}
        placeholder={`np. m[(-2m)^2-2(2m-1)]=3m(2m-1)${d.constant < 0 ? d.constant : `+${d.constant}`}`}
      />
      <InputRow
        label="Krok 3 (1 pkt). Podaj wartosc parametru spelniajaca wszystkie warunki."
        value={userAnswer.final}
        onChange={(v) => setField(userAnswer, "final", v, onChange)}
        disabled={disabled}
        placeholder="np. m=..."
        basis="320px"
      />
    </div>
  );
}

function directVietaPolynomial(d) {
  return `${d.p * d.p === 1 ? "" : d.p * d.p}m^3`
    + signedMonomial(-(d.k + 2) * d.r, "m^2")
    + signedMonomial(-(d.k + 2) * d.s, "m")
    + signedConstant(-d.constant);
}

function checkDirectVietaAnswer(task, answer = {}) {
  const d = getData(task);
  const discriminant = `(${d.p === 1 ? "m" : `${d.p}m`})^2-4(${linearLatex(d.r, d.s)})`;
  const expandedDiscriminant = `${d.p * d.p === 1 ? "" : d.p * d.p}m^2`
    + signedMonomial(-4 * d.r, "m") + signedConstant(-4 * d.s);
  const enteredDelta = normalize(answer.delta);
  const validDelta = [discriminant, expandedDiscriminant].some((form) =>
    [form, `delta=${form}`, `${form}>0`, `delta=${form}>0`].includes(enteredDelta));
  const validVieta = [
    "x1^2+x2^2=(x1+x2)^2-2x1x2",
    `x1^2+x2^2=(${d.p}m)^2-2(${linearLatex(d.r, d.s)})`,
  ].includes(normalize(answer.vietaTransform));
  const parts = [
    { key: "delta", label: "warunek dwóch różnych pierwiastków", correct: validDelta, points: 1 },
    { key: "vietaTransform", label: "zastosowanie wzorów Viète’a", correct: validVieta, points: 1 },
    { key: "equation", label: "równanie w parametrze", correct: normalize(answer.equation) === `${directVietaPolynomial(d)}=0`, points: 1 },
    { key: "final", label: "wynik końcowy", correct: sameVietaValue(answer.final, d.root), points: 1 },
  ];
  const score = parts.filter((part) => part.correct).length;
  return { isCorrect: score === task.points, correct: score === task.points, score, maxScore: task.points, parts };
}

export function checkAnswer(task, userAnswer = {}) {
  if (task.mode === "direct") return checkDirectVietaAnswer(task, userAnswer);
  if (task.source?.variant === "random") return checkAffineAnswer(task, userAnswer);
  let parts = [];
  const d = getData(task);
  const sets = task.variant === "vieta-equation" ? null : expectedSets(task);

  if (task.variant === "same-sign-distance") {
    parts = [
      { key: "delta", label: "warunek Δ > 0", correct: sameVietaSet(userAnswer.delta, sets.delta), points: 1 },
      { key: "sameSign", label: "ten sam znak pierwiastków", correct: sameVietaSet(userAnswer.sameSign, sets.sameSign) || normalize(userAnswer.sameSign) === "(m+8)/(2-m)>0", points: 1 },
      { key: "distanceTransform", label: "przekszta?cenie warunku odleg?o?ci", correct: checkDistanceTransform6(userAnswer.distanceTransform), points: 1 },
      { key: "distanceInequality", label: "nierówność z jedną niewiadomą", correct: normalize(userAnswer.distanceInequality) === `[2(2m+1)/(2-m)]^2-4(m+8)/(2-m)<=${d.limit}` || normalize(userAnswer.distanceInequality) === `20(m+3)(m-1)/(2-m)^2<=${d.limit}`, points: 1 },
      { key: "distanceSolution", label: "rozwiązanie warunku odległości", correct: sameVietaSet(userAnswer.distanceSolution, sets.condition), points: 1 },
      { key: "final", label: "część wspólna warunków", correct: sameVietaSet(userAnswer.final, sets.final), points: 1 },
    ];
  } else if (task.variant === "square-difference") {
    parts = [
      { key: "delta", label: "warunek Δ > 0", correct: sameVietaSet(userAnswer.delta, sets.delta) || normalize(userAnswer.delta) === "4m^2-4m-3>0", points: 1 },
      { key: "absTransform", label: "przekształcenie wartości bezwzględnej", correct: [`|x1-x2|*|x1+x2|<=${d.limit}`, `|x1-x2|<=${d.limit / 3}`].includes(normalize(userAnswer.absTransform)), points: 1 },
      { key: "conditionEquation", label: "nierówność z jedną niewiadomą", correct: [`9-4(-m^2+m+3)<=${(d.limit / 3) ** 2}`, `4m^2-4m-3<=${(d.limit / 3) ** 2}`].includes(normalize(userAnswer.conditionEquation)), points: 1 },
      { key: "conditionSolution", label: "rozwiązanie warunku", correct: sameVietaSet(userAnswer.conditionSolution, sets.condition), points: 1 },
      { key: "final", label: "część wspólna warunków", correct: sameVietaSet(userAnswer.final, sets.final), points: 1 },
    ];
  } else {
    parts = [
      { key: "delta", label: "warunek dwóch różnych pierwiastków", correct: ["m!=1", "m\\ne1"].includes(normalize(userAnswer.delta)), points: 1 },
      { key: "vietaTransform", label: "przekształcenie z wzorów Viète’a", correct: ["x1^2+x2^2=(x1+x2)^2-2x1x2", "x1^2+x2^2=(-2m)^2-2(2m-1)", "x1^2+x2^2=4m^2-4m+2"].includes(normalize(userAnswer.vietaTransform)), points: 1 },
      { key: "equation", label: "równanie z jedną niewiadomą", correct: normalize(userAnswer.equation) === `4m^3-10m^2+5m${d.constant > 0 ? `-${d.constant}` : `+${-d.constant}`}=0` || normalize(userAnswer.equation) === `m[(-2m)^2-2(2m-1)]=3m(2m-1)${d.constant < 0 ? d.constant : `+${d.constant}`}`, points: 1 },
      { key: "final", label: "wynik końcowy", correct: sameVietaValue(userAnswer.final, d.root), points: 1 },
    ];
  }

  const score = parts.reduce((sum, part) => sum + (part.correct ? part.points : 0), 0);
  const maxScore = task.points;

  return {
    isCorrect: score === maxScore,
    correct: score === maxScore,
    score,
    maxScore,
    parts,
  };
}

function checkAffineAnswer(task, answer = {}) {
  const d = getData(task);
  const canonical = expectedSets({ ...task, a: 1, b: 0 });
  const actual = expectedSets(task);
  const s = (key) => normalize(answer[key]);
  const stepSet = (key, name) => sameVietaSet(answer[key], canonical[name]) || sameVietaSet(answer[key], actual[name]);
  let parts;
  if (task.variant === "vieta-equation") {
    const rhs = `3u(2u-1)${d.constant < 0 ? d.constant : `+${d.constant}`}`;
    const expanded = `4u^3-10u^2+5u${d.constant > 0 ? `-${d.constant}` : `+${-d.constant}`}=0`;
    parts = [
      { key: "delta", label: "dwa różne pierwiastki", correct: ["u!=1", `m!=${(1 - d.b) / d.a}`].includes(s("delta")) },
      { key: "vietaTransform", label: "wzory Viète’a", correct: ["x1^2+x2^2=(x1+x2)^2-2x1x2", "x1^2+x2^2=(-2u)^2-2(2u-1)", "x1^2+x2^2=4u^2-4u+2"].includes(s("vietaTransform")) },
      { key: "equation", label: "równanie w u", correct: [expanded, `u[(-2u)^2-2(2u-1)]=${rhs}`].includes(s("equation")) },
      { key: "final", label: "wynik końcowy", correct: sameVietaValue(answer.final, (d.root - d.b) / d.a) },
    ];
  } else if (task.variant === "same-sign-distance") {
    parts = [
      { key: "delta", label: "dwa różne miejsca zerowe", correct: stepSet("delta", "delta") },
      { key: "sameSign", label: "ten sam znak miejsc zerowych", correct: stepSet("sameSign", "sameSign") || s("sameSign") === "(u+8)/(2-u)>0" },
      { key: "distanceTransform", label: "wzór na różnicę pierwiastków", correct: checkDistanceTransform6(answer.distanceTransform) },
      { key: "distanceInequality", label: "nierówność w u", correct: [`[2(2u+1)/(2-u)]^2-4(u+8)/(2-u)<=${d.limit}`, `20(u+3)(u-1)/(2-u)^2<=${d.limit}`].includes(s("distanceInequality")) },
      { key: "distanceSolution", label: "warunek odległości", correct: stepSet("distanceSolution", "condition") },
      { key: "final", label: "część wspólna warunków", correct: sameVietaSet(answer.final, actual.final) },
    ];
  } else {
    const c = (d.sum * d.sum + 3) / 4;
    parts = [
      { key: "delta", label: "dwa różne miejsca zerowe", correct: stepSet("delta", "delta") || s("delta") === "4u^2-4u-3>0" },
      { key: "absTransform", label: "różnica kwadratów pierwiastków", correct: [`|x1-x2|*|x1+x2|<=${d.limit}`, `|x1-x2|<=${d.gap}`].includes(s("absTransform")) },
      { key: "conditionEquation", label: "nierówność w u", correct: [`${d.sum}^2-4(-u^2+u+${c})<=${d.gap ** 2}`, `4u^2-4u-3<=${d.gap ** 2}`].includes(s("conditionEquation")) },
      { key: "conditionSolution", label: "warunek z wartości bezwzględnej", correct: stepSet("conditionSolution", "condition") },
      { key: "final", label: "część wspólna warunków", correct: sameVietaSet(answer.final, actual.final) },
    ];
  }
  const scored = parts.map((part) => ({ ...part, points: 1 }));
  const score = scored.filter((part) => part.correct).length;
  return { isCorrect: score === task.points, correct: score === task.points, score, maxScore: task.points, parts: scored };
}

function renderLine(label, value) {
  return (
    <p>
      <strong>{label}:</strong>{" "}
      <InlineMath math={toLatexExpression(value)} />
    </p>
  );
}

export function renderUserAnswer(userAnswer = {}, task) {
  if (task?.variant === "same-sign-distance") {
    return (
      <div className="space-y-1">
        {renderLine("Warunek Δ", userAnswer.delta)}
        {renderLine("Ten sam znak", userAnswer.sameSign)}
        {renderLine("Przekształcenie odległości", userAnswer.distanceTransform)}
        {renderLine("Nierówność", userAnswer.distanceInequality)}
        {renderLine("Rozwiązanie warunku", userAnswer.distanceSolution)}
        {renderLine("Odpowiedź", userAnswer.final)}
      </div>
    );
  }
  if (task?.variant === "square-difference") {
    return (
      <div className="space-y-1">
        {renderLine("Warunek Δ", userAnswer.delta)}
        {renderLine("Przekształcenie", userAnswer.absTransform)}
        {renderLine("Nierówność", userAnswer.conditionEquation)}
        {renderLine("Rozwiązanie warunku", userAnswer.conditionSolution)}
        {renderLine("Odpowiedź", userAnswer.final)}
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {renderLine("Warunek Δ", userAnswer.delta)}
      {renderLine("Przekształcenie", userAnswer.vietaTransform)}
      {renderLine("Równanie", userAnswer.equation)}
      {renderLine("Odpowiedź", userAnswer.final)}
    </div>
  );
}

export function renderAnswer(task) {
  const d = getData(task);
  if (task.mode === "direct") return <p><strong>Odpowiedź:</strong> <InlineMath math={`m=${d.root}`} /></p>;
  if (task.source?.variant !== "original") {
    if (task.variant === "vieta-equation") {
      return <div className="space-y-2">
        <p><strong>Podstawienie:</strong> <InlineMath math={`u=${linearLatex(d.a, d.b)}`} /></p>
        <p><strong>Wynik dla <InlineMath math="u"/>:</strong> <InlineMath math={`u=${d.root}`} /></p>
        <p><strong>Odpowiedź:</strong> <InlineMath math={`m=${rationalLatex(d.root - d.b, d.a)}`} /></p>
      </div>;
    }
    if (task.variant === "same-sign-distance") {
      const L = (u, tex) => mEndpointLatex(u, d.a, d.b, tex);
      return <div className="space-y-2">
        <p><strong>Podstawienie:</strong> <InlineMath math={`u=${linearLatex(d.a, d.b)}`} /></p>
        <p><strong>Wynik dla <InlineMath math="u"/>:</strong> <InlineMath math={`u\\in(-8,-3)\\cup(1,${d.lowLatex}]`} /></p>
        <p><strong>Odpowiedź:</strong> <InlineMath math={`m\\in(${L(-8, "-8")},${L(-3, "-3")})\\cup(${L(1, "1")},${L(d.low, d.lowLatex)}]`} /></p>
      </div>;
    }
    return <div className="space-y-2">
      <p><strong>Podstawienie:</strong> <InlineMath math={`u=${linearLatex(d.a, d.b)}`} /></p>
      <p><strong>Wynik dla <InlineMath math="u"/>:</strong> <InlineMath math={`u\\in[${d.lowLatex},-\\frac12)\\cup(\\frac32,${d.highLatex}]`} /></p>
      <p><strong>Odpowiedź:</strong> <InlineMath math={`m\\in[${squareMEndpointLatex(d, -1)},${rationalLatex(-1 - 2 * d.b, 2 * d.a)})\\cup(${rationalLatex(3 - 2 * d.b, 2 * d.a)},${squareMEndpointLatex(d, 1)}]`} /></p>
    </div>;
  }
  if (task.variant === "same-sign-distance") {
    return (
      <div className="space-y-2">
        <p><strong>Warunek <InlineMath math="\Delta"/>:</strong> <InlineMath math="m\in(-\infty,-3)\cup(1,+\infty)" /></p>
        <p><strong>Ten sam znak:</strong> <InlineMath math="m\in(-8,2)" /></p>
        <p><strong>Warunek odległości:</strong> <InlineMath math={`m\\in(-\\infty,${d.lowLatex}]\\cup[${d.highLatex},+\\infty)`} /></p>
        <p><strong>Odpowiedź:</strong> <InlineMath math={`m\\in(-8,-3)\\cup(1,${d.lowLatex}]`} /></p>
      </div>
    );
  }
  if (task.variant === "square-difference") {
    return (
      <div className="space-y-2">
        <p><strong>Warunek <InlineMath math="\Delta"/>:</strong> <InlineMath math="m\in(-\infty,-\frac12)\cup(\frac32,+\infty)" /></p>
        <p><strong>Warunek z wartości bezwzględnej:</strong> <InlineMath math={`m\\in\\left[${d.lowLatex},${d.highLatex}\\right]`} /></p>
        <p><strong>Odpowiedź:</strong> <InlineMath math={`m\\in\\left[${d.lowLatex},-\\frac12\\right)\\cup\\left(\\frac32,${d.highLatex}\\right]`} /></p>
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <p><strong>Warunek <InlineMath math="\Delta"/>:</strong> <InlineMath math="m\ne 1" /></p>
      <p><strong>Równanie:</strong> <InlineMath math={`m\\left[(-2m)^2-2(2m-1)\\right]=3m(2m-1)${d.constant < 0 ? d.constant : `+${d.constant}`}`} /></p>
      <p><strong>Odpowiedź:</strong> <InlineMath math={`m=${d.root}`} /></p>
    </div>
  );
}

function renderVietaEquationExplanation(task) {
  const { root, constant } = getData(task);
  const linear = 4 * root - 10;
  const free = 4 * root * root - 10 * root + 5;
  const signed = (n) => n < 0 ? String(n) : `+${n}`;
  return (
    <div className="space-y-4">
      <p><strong>Krok 1.</strong> Sprawdzamy warunek dwóch różnych pierwiastków.</p>
      <BlockMath math="\Delta=(2m)^2-4(2m-1)=4(m-1)^2" />
      <BlockMath math="\Delta>0\quad\Longleftrightarrow\quad m\ne 1" />

      <p><strong>Krok 2.</strong> Z wzorów Viete'a mamy</p>
      <BlockMath math="x_1+x_2=-2m,\qquad x_1x_2=2m-1" />
      <p>Sumę kwadratów zapisujemy przez sumę i iloczyn pierwiastków:</p>
      <BlockMath math="x_1^2+x_2^2=(x_1+x_2)^2-2x_1x_2=(-2m)^2-2(2m-1)" />
      <BlockMath math={`m\\left[(-2m)^2-2(2m-1)\\right]=3m(2m-1)${signed(constant)}`} />
      <BlockMath math={`4m^3-10m^2+5m${signed(-constant)}=0`} />
      <BlockMath math={`(m${signed(-root)})(4m^2${signed(linear)}m${signed(free)})=0`} />

      <p>
        Wyróżnik trójmianu <InlineMath math={`4m^2${signed(linear)}m${signed(free)}`} /> jest
        ujemny, więc jedynym kandydatem jest <InlineMath math={`m=${root}`} />.
      </p>
      <BlockMath math={`\\Delta_1=(${linear})^2-16\\cdot(${free})=${linear * linear - 16 * free}<0`} />
      <p>
        Ponieważ <InlineMath math={`${root}\\ne 1`} />, warunek{" "}
        <InlineMath math="\Delta>0" /> jest spełniony. Odpowiedź:{" "}
        <InlineMath math={`m=${root}`} />.
      </p>
    </div>
  );
}

function renderDirectVietaExplanation(task) {
  const d = getData(task);
  const pTerm = d.p === 1 ? "m" : `${d.p}m`;
  const product = linearLatex(d.r, d.s);
  const middle = -(d.k + 2) * d.r + d.p * d.p * d.root;
  const free = -(d.k + 2) * d.s - (d.k + 2) * d.r * d.root + d.p * d.p * d.root * d.root;
  const quadratic = `${d.p * d.p === 1 ? "" : d.p * d.p}m^2${signedMonomial(middle, "m")}${signedConstant(free)}`;
  const qDiscriminant = middle * middle - 4 * d.p * d.p * free;
  const originalDiscriminant = (d.p * d.root) ** 2 - 4 * (d.r * d.root + d.s);
  return <div className="space-y-4">
    <p><strong>Krok 1.</strong> Dwa różne pierwiastki wymagają dodatniego wyróżnika:</p>
    <BlockMath math={`\\Delta=(${pTerm})^2-4(${product})>0`} />
    <p><strong>Krok 2.</strong> Z wzorów Viète’a otrzymujemy sumę i iloczyn pierwiastków:</p>
    <BlockMath math={`x_1+x_2=-${pTerm}`} />
    <BlockMath math={`x_1x_2=${product}`} />
    <BlockMath math={`x_1^2+x_2^2=(x_1+x_2)^2-2x_1x_2=(${pTerm})^2-2(${product})`} />
    <p>Wstawiamy te wyrażenia do warunku z treści zadania i przenosimy wszystko na jedną stronę:</p>
    <BlockMath math={`m[(${pTerm})^2-2(${product})]=${d.k}m(${product})${signedConstant(d.constant)}`} />
    <BlockMath math={`${directVietaPolynomial(d)}=0`} />
    <p><strong>Krok 3.</strong> Wielomian rozkłada się na czynnik liniowy i trójmian kwadratowy:</p>
    <BlockMath math={`(m${signedConstant(-d.root)})(${quadratic})=0`} />
    <BlockMath math={`\\Delta_Q=${qDiscriminant}<0`} />
    <p>Trójmian nie ma rzeczywistych miejsc zerowych, więc jedynym kandydatem jest <InlineMath math={`m=${d.root}`} />.</p>
    <p><strong>Krok 4.</strong> Sprawdzamy pominięty dotąd warunek dwóch różnych pierwiastków:</p>
    <BlockMath math={`\\Delta(${d.root})=${originalDiscriminant}>0`} />
    <p>Warunek jest spełniony. <strong>Odpowiedź:</strong> <InlineMath math={`m=${d.root}`} />.</p>
  </div>;
}

function renderSameSignExplanation(task) {
  const d = getData(task);
  return (
    <div className="space-y-4">
      <p><strong>Krok 1.</strong> Warunek dwóch różnych miejsc zerowych:</p>
      <BlockMath math="a=2-m,\quad b=-2(2m+1),\quad c=m+8" />
      <BlockMath math="\Delta>0\quad\Longleftrightarrow\quad m\in(-\infty,-3)\cup(1,+\infty)" />

      <p><strong>Krok 2.</strong> Miejsca zerowe mają ten sam znak, gdy ich iloczyn jest dodatni.</p>
      <BlockMath math="x_1x_2=\frac{c}{a}=\frac{m+8}{2-m}>0" />
      <BlockMath math="m\in(-8,2)" />

      <p><strong>Krok 3.</strong> Korzystamy ze wzoru</p>
      <BlockMath math="(x_1-x_2)^2=(x_1+x_2)^2-4x_1x_2" />
      <BlockMath math={`\\left[\\frac{2(2m+1)}{2-m}\\right]^2-4\\cdot\\frac{m+8}{2-m}\\le ${d.limit}`} />
      <BlockMath math={`\\frac{20(m+3)(m-1)}{(2-m)^2}\\le ${d.limit}`} />
      <p>Ponieważ mianownik jest dodatni dla <InlineMath math="m\ne2" />, mnożymy przez niego bez zmiany znaku nierówności.</p>
      <BlockMath math={`${d.limit - 20}m^2-${4 * d.limit + 40}m+${4 * d.limit + 60}\\ge0`} />
      <p>Trójmian ma miejsca zerowe <InlineMath math={`m=${d.lowLatex}`} /> i <InlineMath math={`m=${d.highLatex}`} />. Ramiona paraboli są skierowane w górę.</p>
      <BlockMath math={`m\\in(-\\infty,${d.lowLatex}]\\cup[${d.highLatex},+\\infty)`} />

      <p><strong>Krok 4.</strong> Bierzemy część wspólną wszystkich warunków, pamiętając też, że <InlineMath math="m\ne2" />.</p>
      <BlockMath math={`m\\in(-8,-3)\\cup(1,${d.lowLatex}]`} />
    </div>
  );
}

function renderSquareDifferenceExplanation(task) {
  const d = getData(task);
  return (
    <div className="space-y-4">
      <p><strong>Krok 1.</strong> Warunek dwóch różnych miejsc zerowych:</p>
      <BlockMath math="\Delta=(-3)^2-4(-m^2+m+3)=4m^2-4m-3" />
      <BlockMath math="\Delta>0\quad\Longleftrightarrow\quad m\in(-\infty,-\frac12)\cup(\frac32,+\infty)" />

      <p><strong>Krok 2.</strong> Z wzorów Viete'a:</p>
      <BlockMath math="x_1+x_2=3" />
      <BlockMath math="x_1x_2=-m^2+m+3" />
      <p>Warunek przekształcamy tak, żeby użyć sumy i iloczynu pierwiastków.</p>
      <BlockMath math={`|x_1^2-x_2^2|=|x_1-x_2|\\cdot|x_1+x_2|\\le ${d.limit}`} />
      <BlockMath math={`|x_1+x_2|=3,\\quad \\text{więc}\\quad |x_1-x_2|\\le${d.limit / 3}`} />
      <BlockMath math={`(x_1-x_2)^2\\le${(d.limit / 3) ** 2}`} />
      <BlockMath math="(x_1-x_2)^2=(x_1+x_2)^2-4x_1x_2" />
      <BlockMath math={`9-4(-m^2+m+3)\\le${(d.limit / 3) ** 2}`} />
      <BlockMath math={`4m^2-4m-3\\le${(d.limit / 3) ** 2}`} />
      <p>Rozwiązując nierówność kwadratową, otrzymujemy przedział pomiędzy jej dwoma pierwiastkami:</p>
      <BlockMath math={`m\\in\\left[${d.lowLatex},${d.highLatex}\\right]`} />

      <p><strong>Krok 3.</strong> Bierzemy część wspólną obu warunków.</p>
      <BlockMath math={`m\\in\\left[${d.lowLatex},-\\frac12\\right)\\cup\\left(\\frac32,${d.highLatex}\\right]`} />
    </div>
  );
}

function renderAffineExplanation(task) {
  const d = getData(task);
  const relation = `u=${linearLatex(d.a, d.b)}`;
  const finalAnswer = renderAnswer(task);
  if (task.variant === "vieta-equation") {
    const linear = 4 * d.root - 10;
    const free = 4 * d.root * d.root - 10 * d.root + 5;
    const signed = (n) => n < 0 ? String(n) : `+${n}`;
    return <div className="space-y-4">
      <p><strong>Krok 1.</strong> Podstawiamy <InlineMath math={relation} />. Dzięki temu równanie i warunek mają postać znaną z oryginału.</p>
      <BlockMath math="x^2+2ux+2u-1=0" />
      <BlockMath math="\Delta=4(u-1)^2>0\quad\Longleftrightarrow\quad u\ne1" />
      <p><strong>Krok 2.</strong> Ze wzorów Viète’a: <InlineMath math="x_1+x_2=-2u" /> oraz <InlineMath math="x_1x_2=2u-1" />. Stąd:</p>
      <BlockMath math="x_1^2+x_2^2=(x_1+x_2)^2-2x_1x_2=4u^2-4u+2" />
      <BlockMath math={`u(4u^2-4u+2)=3u(2u-1)${signed(d.constant)}`} />
      <BlockMath math={`4u^3-10u^2+5u${signed(-d.constant)}=0`} />
      <BlockMath math={`(u${signed(-d.root)})(4u^2${signed(linear)}u${signed(free)})=0`} />
      <p>Drugi czynnik nie ma pierwiastków rzeczywistych: <InlineMath math={`\\Delta_1=(${linear})^2-16(${free})=${linear * linear - 16 * free}<0`} />. Zostaje <InlineMath math={`u=${d.root}`} />, które spełnia <InlineMath math="u\ne1" />.</p>
      <p><strong>Krok 3.</strong> Wracamy do parametru <InlineMath math="m" />:</p>
      <BlockMath math={`${linearLatex(d.a, d.b)}=${d.root}\\quad\\Longrightarrow\\quad m=${rationalLatex(d.root - d.b, d.a)}`} />
      {finalAnswer}
    </div>;
  }
  if (task.variant === "same-sign-distance") {
    return <div className="space-y-4">
      <p><strong>Krok 1.</strong> Przyjmujemy <InlineMath math={relation} />. Wtedy:</p>
      <BlockMath math="f(x)=(2-u)x^2-2(2u+1)x+u+8" />
      <BlockMath math="\Delta>0\quad\Longleftrightarrow\quad u\in(-\infty,-3)\cup(1,+\infty)" />
      <p><strong>Krok 2.</strong> Iloczyn pierwiastków jest dodatni dokładnie wtedy, gdy mają ten sam znak.</p>
      <BlockMath math="x_1x_2=\frac{u+8}{2-u}>0\quad\Longleftrightarrow\quad u\in(-8,2)" />
      <p><strong>Krok 3.</strong> Warunek odległości przepisujemy przez sumę i iloczyn pierwiastków.</p>
      <BlockMath math="(x_1-x_2)^2=(x_1+x_2)^2-4x_1x_2" />
      <BlockMath math={`\\frac{20(u+3)(u-1)}{(2-u)^2}\\le${d.limit}`} />
      <BlockMath math={`${d.limit - 20}u^2-${4 * d.limit + 40}u+${4 * d.limit + 60}\\ge0`} />
      <p>Ten trójmian jest nieujemny poza przedziałem między pierwiastkami <InlineMath math={d.lowLatex} /> i <InlineMath math={d.highLatex} />. Część wspólna trzech warunków to:</p>
      <BlockMath math={`u\\in(-8,-3)\\cup(1,${d.lowLatex}]`} />
      <p><strong>Krok 4.</strong> Z <InlineMath math={relation} /> wyznaczamy granice przedziałów dla <InlineMath math="m" />:</p>
      {finalAnswer}
    </div>;
  }
  const c = (d.sum * d.sum + 3) / 4;
  return <div className="space-y-4">
    <p><strong>Krok 1.</strong> Oznaczamy <InlineMath math={relation} />. Funkcja ma wtedy wzór:</p>
    <BlockMath math={`f(x)=x^2-${d.sum}x-u^2+u+${c}`} />
    <BlockMath math="\Delta=4u^2-4u-3>0\quad\Longleftrightarrow\quad u\in(-\infty,-\frac12)\cup(\frac32,+\infty)" />
    <p><strong>Krok 2.</strong> Ze wzorów Viète’a <InlineMath math={`x_1+x_2=${d.sum}`} />. Dlatego:</p>
    <BlockMath math={`|x_1^2-x_2^2|=|x_1-x_2|\\cdot|x_1+x_2|=${d.sum}|x_1-x_2|\\le${d.limit}`} />
    <BlockMath math={`|x_1-x_2|\\le${d.gap}\\quad\\Longrightarrow\\quad 4u^2-4u-3\\le${d.gap ** 2}`} />
    <BlockMath math={`u\\in[${d.lowLatex},${d.highLatex}]`} />
    <p><strong>Krok 3.</strong> Bierzemy część wspólną z <InlineMath math="\Delta>0" />, a potem rozwiązujemy <InlineMath math={relation} /> względem <InlineMath math="m" />:</p>
    <BlockMath math={`u\\in[${d.lowLatex},-\\frac12)\\cup(\\frac32,${d.highLatex}]`} />
    {finalAnswer}
  </div>;
}

export function renderExplanation(task) {
  if (task.mode === "direct") return renderDirectVietaExplanation(task);
  if (task.source?.variant === "random") return renderAffineExplanation(task);
  if (task.variant === "same-sign-distance") return renderSameSignExplanation(task);
  if (task.variant === "square-difference") return renderSquareDifferenceExplanation(task);
  return renderVietaEquationExplanation(task);
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderUserAnswer,
  renderExplanation,
};
